import React, { useMemo } from 'react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Answer } from 'api/Clients/Types/TestDto'
import { QuestionDto, TestQuestionWidgetType } from 'api/Clients/Types/TestQuestionDto'
import classNames from 'classnames'
import { Input } from 'components/Input'
import styles from 'styles/components/QuestionAnswerSection/questionAnswerSection.module.scss'
import { truncateString } from 'utils/string.utils'

import { Box } from '../Box'
import { Caption2 } from '../ui/typography'

interface SectionProps {
  label: string
  sectionScore: number | null
  items: Answer[]
  showScore: boolean
}

export const QuestionAnswerSection: React.FC<SectionProps> = (props) => {
  const { label = 'Label', sectionScore, showScore, items } = props

  interface NewAnswers {
    [key: string]: {
      question: QuestionDto
      answers: Answer['answer'][]
    }
  }

  const parsedAnswers = useMemo(() => {
    const newAnswers: NewAnswers = {}
    items.forEach((answer) => {
      if (newAnswers[answer.question.id]) {
        newAnswers[answer.question.id].answers.push(answer.answer)
      } else {
        newAnswers[answer.question.id] = {
          question: answer.question,
          answers: [answer.answer],
        }
      }
    })

    return Object.keys(newAnswers)
      .map((key) => ({
        ...newAnswers[key],
        answers: newAnswers[key].answers.sort((a, b) =>
          a.code && b.code ? a.code.localeCompare(b.code) : 0
        ),
      }))
      .sort((a, b) => a.question.order - b.question.order)
  }, [items])

  return (
    <SectionContainer isNewWindow>
      <label>{label}</label>
      {parsedAnswers.map((answer) => (
        <WidgetFactory answer={answer} showScore={showScore} />
      ))}
      {showScore && <span className={styles.totalScore}>Section score: {sectionScore}</span>}
    </SectionContainer>
  )
}

interface SectionContainerProps {
  children: React.ReactNode
  isNewWindow?: boolean
}

export const SectionContainer = ({ children, isNewWindow }: SectionContainerProps) => (
  <section className={classNames(styles.sectionContainer, { [styles.isNewWindow]: isNewWindow })}>
    {children}
  </section>
)

interface WidgetFactoryProps {
  answer: { answers: Answer['answer'][]; question: QuestionDto }
  showScore: boolean
}

const WidgetFactory: React.FC<WidgetFactoryProps> = (props) => {
  const { answer, showScore } = props

  switch (answer.question.widgetType) {
    case TestQuestionWidgetType.Checkbox:
      return <CheckboxWidget answer={answer} showScore={showScore} />
    case TestQuestionWidgetType.MultiSelect:
      return <MultiSelectWidget answer={answer} showScore={showScore} />
    case TestQuestionWidgetType.Radio:
      return <RadioWidget answer={answer} showScore={showScore} />
    default:
      return <InputWidget answer={answer} showScore={showScore} key={answer.question.id} />
  }
}

const RadioWidget: React.FC<WidgetFactoryProps> = ({ answer, showScore }) => {
  const checkedAnswer =
    answer.answers.find((ans) => ans.id) ?? answer.answers.find((ans) => ans.name)
  const checkedValue = checkedAnswer?.id || checkedAnswer?.name || ''

  return (
    <div key={answer.question.id}>
      <span>{answer.question.name}</span>

      <RadioGroup disabled value={checkedValue} className='flex flex-col gap-1 mt-2 px-0'>
        {answer.answers.map((ans) => {
          const value = ans.id || ans.name

          return (
            <div className={styles.inputWrapper} key={value}>
              <div className={styles.multiSelect}>
                <RadioGroupItem value={value} />
                <span className='text-sm'>{ans.name}</span>
              </div>

              {showScore && <span className={styles.score}>Score: {ans.score || 0}</span>}
            </div>
          )
        })}
      </RadioGroup>
    </div>
  )
}

const CheckboxWidget: React.FC<WidgetFactoryProps> = ({ answer, showScore }) => (
  <div key={answer.question.id}>
    <span>{answer.question.name}</span>
    {answer.answers.map((ans) => (
      <div className={styles.inputWrapper}>
        <Input checked={ans.name === '1'} type='checkbox' disabled />
        {showScore && <span className={styles.score}>Score: {ans.score || 0}</span>}
      </div>
    ))}
  </div>
)

const MultiSelectWidget: React.FC<WidgetFactoryProps> = ({ answer, showScore }) => (
  <div key={answer.question.id}>
    <span>{answer.question.name}</span>
    {answer.answers.map((ans) => (
      <div className={styles.inputWrapper}>
        <div className={styles.multiSelect}>
          <Input checked type='checkbox' disabled />
          <span>{ans.name}</span>
        </div>
        {showScore && <span className={styles.score}>Score: {ans.score || 0}</span>}
      </div>
    ))}
  </div>
)

const InputWidget: React.FC<WidgetFactoryProps> = ({ answer, showScore }) => (
  <div key={answer.question.id}>
    <span>{answer.question.name}</span>
    {answer.answers.map((ans) => (
      <div className={styles.inputWrapper}>
        {isWidgetTextAreaType(ans) ? (
          <textarea name='name' value={ans.name} disabled rows={3} />
        ) : (
          <Input value={ans.name} type='text' disabled />
        )}
        {showScore && <span className={styles.score}>Score: {ans.score || 0}</span>}
      </div>
    ))}
  </div>
)

const isWidgetTextAreaType = (answer: Answer['answer']) => {
  return answer.name.length > 100
}

interface DocumentSectionProps {
  docs: { id: string; name: string }[]

  onClick(id: string, name: string): void
}

export const DocumentSection: React.FC<DocumentSectionProps> = ({ docs, onClick }) => (
  <SectionContainer>
    <label>Documents</label>
    <ul>
      {docs.map((doc) => {
        return (
          <div key={doc.id}>
            <span className={styles.documentName} onClick={() => onClick(doc.id, doc.name)}>
              <li>{truncateString(doc.name, 50)}</li>
            </span>
          </div>
        )
      })}
    </ul>
  </SectionContainer>
)

interface TotalScoreProps {
  totalScore: number
  scoreNote: string
}

export const TotalScoreContainer = ({ children }: { children: React.ReactNode }) => (
  <span className={styles.totalScoreContainer}>{children}</span>
)

export const TotalScore: React.FC<TotalScoreProps> = ({ totalScore, scoreNote }) => (
  <Box display='flex' flexDirection='column' alignItems='flex-end'>
    <TotalScoreContainer>Total score: {totalScore}</TotalScoreContainer>
    <Caption2>{scoreNote}</Caption2>
  </Box>
)
